import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, PlayCircle, TrendingUp, AlertTriangle, CheckCircle } from "lucide-react";

export default function Statistics() {
  const [nReturns, setNReturns] = useState(200);
  const [wfResult, setWfResult] = useState(null);
  const [pboResult, setPboResult] = useState(null);
  const [wfLoading, setWfLoading] = useState(false);
  const [pboLoading, setPboLoading] = useState(false);

  const genReturns = (n) => {
    // Cheap PRNG for reproducibility
    let seed = 42;
    const rand = () => { seed = (seed * 9301 + 49297) % 233280; return seed / 233280; };
    const gauss = () => Math.sqrt(-2 * Math.log(rand())) * Math.cos(2 * Math.PI * rand());
    return Array.from({ length: n }, () => 0.001 + 0.02 * gauss());
  };

  const runWF = async () => {
    setWfLoading(true);
    try {
      const rets = genReturns(nReturns);
      const r = await api.walkForward(rets, 0.6, 0.1);
      setWfResult(r);
    } catch (e) { toast.error("Erreur walk-forward"); }
    finally { setWfLoading(false); }
  };

  const runPBO = async () => {
    setPboLoading(true);
    try {
      // Generate 4 strategies with slightly different means to test PBO
      const nObs = 80;
      const strats = Array.from({ length: 4 }, (_, k) => {
        let seed = 42 + k * 7;
        const rand = () => { seed = (seed * 9301 + 49297) % 233280; return seed / 233280; };
        const gauss = () => Math.sqrt(-2 * Math.log(rand())) * Math.cos(2 * Math.PI * rand());
        const mu = 0.001 * (k + 1); // slight edge increase per strategy
        return Array.from({ length: nObs }, () => mu + 0.02 * gauss());
      });
      // Transpose: pbo expects observations × strategies
      const matrix = Array.from({ length: nObs }, (_, i) => strats.map((s) => s[i]));
      const r = await api.pbo(matrix, 8);
      setPboResult(r);
    } catch (e) { toast.error("Erreur PBO"); }
    finally { setPboLoading(false); }
  };

  return (
    <div className="p-6 space-y-6" data-testid="statistics-page">
      <div>
        <h1 className="font-heading font-bold text-2xl tracking-tight">Statistical Significance Suite</h1>
        <p className="text-white/50 text-sm mt-1">Walk-Forward, PBO (Bailey-Borwein-López-Zhu), DSR, White's Reality Check. Les standards de la recherche quantitative publiée.</p>
      </div>

      {/* Walk-Forward */}
      <div className="panel p-5">
        <div className="flex justify-between mb-4 items-end">
          <div>
            <div className="panel-title"><TrendingUp size={14} className="inline mr-2" />Walk-Forward Optimization</div>
            <p className="text-xs text-white/50 mt-1">Split sequential windows, mesure la dégradation train↔test out-of-sample.</p>
          </div>
          <div className="flex gap-2 items-end">
            <div>
              <label className="label">N returns (démo)</label>
              <input type="number" className="field w-24" value={nReturns} onChange={(e) => setNReturns(parseInt(e.target.value))} data-testid="wf-n-returns" />
            </div>
            <button className="btn btn-primary" onClick={runWF} disabled={wfLoading} data-testid="wf-run-btn">
              {wfLoading ? <Loader2 size={14} className="animate-spin" /> : <PlayCircle size={14} />} Lancer
            </button>
          </div>
        </div>

        {wfResult?.ok && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="wf-results">
              <Stat label="Windows" value={wfResult.n_windows} />
              <Stat label="Train SR (annualisé)" value={wfResult.avg_train_sharpe} />
              <Stat label="Test SR (annualisé)" value={wfResult.avg_test_sharpe}
                    color={wfResult.avg_test_sharpe > 0 ? "up" : "down"} />
              <Stat label="Test SR (daily)" value={wfResult.avg_test_daily_sharpe} highlight />
            </div>
            <div className="mt-3">
              <div className="text-xs text-white/50">Consistency out-of-sample</div>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-2 bg-white/5 rounded">
                  <div className="h-full bg-up" style={{ width: `${wfResult.consistency_pct}%` }} />
                </div>
                <div className="text-sm font-mono">{wfResult.consistency_pct}%</div>
              </div>
            </div>
            {wfResult.warnings?.length > 0 && (
              <div className="mt-3 p-3 bg-warn/10 border border-warn/30 rounded text-xs text-warn" data-testid="wf-warnings">
                <AlertTriangle size={12} className="inline mr-1" />
                {wfResult.warnings.join(" ")}
              </div>
            )}
            <SampleBadge n={nReturns} />
            <div className="mt-3 text-sm text-white/70">{wfResult.interpretation}</div>
          </>
        )}
      </div>

      {/* PBO */}
      <div className="panel p-5">
        <div className="flex justify-between mb-4 items-end">
          <div>
            <div className="panel-title"><CheckCircle size={14} className="inline mr-2" />PBO — Probability of Backtest Overfitting</div>
            <p className="text-xs text-white/50 mt-1">Bailey-Borwein-López-Zhu 2015. Combinatorial K-Fold, mesure la probabilité que la meilleure stratégie IS soit sub-médiane OOS.</p>
          </div>
          <button className="btn btn-primary" onClick={runPBO} disabled={pboLoading} data-testid="pbo-run-btn">
            {pboLoading ? <Loader2 size={14} className="animate-spin" /> : <PlayCircle size={14} />} Lancer (4 stratégies × 80 obs)
          </button>
        </div>

        {pboResult?.ok && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="pbo-results">
              <Stat label="PBO" value={pboResult.pbo}
                    color={pboResult.pbo < 0.25 ? "up" : pboResult.pbo > 0.5 ? "down" : "warn"} highlight />
              <Stat label="Combos testés" value={pboResult.n_combinations} />
              <Stat label="Strategies" value={pboResult.n_strategies} />
              <Stat label="Médiane OOS rank" value={pboResult.median_oos_rel_rank} />
            </div>
            <div className={`mt-3 p-3 rounded text-sm ${
              pboResult.pbo < 0.25 ? "bg-up/10 text-up" :
              pboResult.pbo > 0.5 ? "bg-down/10 text-down" : "bg-warn/10 text-warn"
            }`}>
              {pboResult.interpretation}
            </div>
            <SampleBadge n={80} />
          </>
        )}
      </div>
    </div>
  );
}

function SampleBadge({ n }) {
  if (n >= 100) return null;
  return (
    <div className="mt-3 p-3 bg-warn/10 border border-warn/30 rounded text-xs text-warn" data-testid="sample-size-warning">
      <AlertTriangle size={12} className="inline mr-1" />
      Échantillon insuffisant (n={n} &lt; 100) — la puissance statistique des tests est limitée. Un
      résultat non significatif ne prouve pas l&apos;absence d&apos;edge ; un résultat significatif peut être instable.
    </div>
  );
}

function Stat({ label, value, color, highlight }) {
  return (
    <div className={`p-3 rounded ${highlight ? "bg-accent-blue/10 border border-accent-blue/30" : "bg-white/5"}`}>
      <div className="text-xs uppercase tracking-widest text-white/50">{label}</div>
      <div className={`font-mono text-lg mt-1 font-bold ${
        color === "up" ? "text-up" : color === "down" ? "text-down" :
        color === "warn" ? "text-warn" : highlight ? "text-accent-blue" : ""
      }`}>{value}</div>
    </div>
  );
}
